import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './MarketingPlan.module.css';

const MarketingPlan = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    // Check authentication
    const storedToken = sessionStorage.getItem('clientAuth');
    
    if (!storedToken || storedToken !== token) {
      navigate('/client-login', { replace: true });
      return;
    }
    
    setIsAuthenticated(true);
    setLoading(false);
  }, [token, navigate]);

  const handleBackToDashboard = () => {
    navigate(`/dashboard/${token}`);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('clientAuth');
    navigate('/client-login', { replace: true });
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loader}></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const availableServices = [
    {
      title: 'Core Growth Sprint Bundle',
      duration: '8 weeks',
      description: [
        'GA4 & Tag Manager configuration ($1,000 value)',
        'Google Ads build + 6-week optimization loop ($2,250 value)',
        '3 SEO blog posts with keyword research ($500 value)',
        '15-second promo animation for video ads ($2,000 value)',
        'Conversion tracking implementation (included)'
      ],
      price: '4500',
      originalPrice: '5750',
      isBundle: true,
      savings: '1250'
    },
    {
      title: 'Google Ads Management',
      duration: 'Month-to-month',
      description: [
        'Ongoing bid and creative optimization',
        'Negative keyword management',
        'A/B testing of ad copy',
        'Monthly performance reports',
        'Budget optimization'
      ],
      price: '850'
    },
    {
      title: 'SEO Content Strategy',
      duration: '4-6 weeks',
      description: [
        'Comprehensive keyword research',
        'Content calendar development',
        'On-page SEO optimization',
        'Meta descriptions and titles',
        'Internal linking strategy'
      ],
      price: '1200'
    },
    {
      title: 'Blog SEO Overhaul',
      duration: '1-2 weeks',
      description: [
        'Rewrite 3 existing articles',
        'Keyword mapping and optimization',
        'Meta tags and descriptions',
        'Custom images and graphics',
        'Internal linking improvements'
      ],
      price: '500'
    },
    {
      title: 'Social Media Marketing',
      duration: '3 months',
      description: [
        'Content strategy development',
        'Weekly posting schedule',
        'Engagement management',
        'Hashtag research and optimization',
        'Monthly analytics reports'
      ],
      price: '2200'
    },
    {
      title: 'Email Marketing Campaign',
      duration: '2-3 weeks',
      description: [
        'Email sequence design',
        'List segmentation strategy',
        'Automated drip campaigns',
        'A/B testing of subject lines',
        'Performance tracking setup'
      ],
      price: '800'
    },
    {
      title: 'Conversion Rate Optimization',
      duration: '4-6 weeks',
      description: [
        'Landing page analysis',
        'A/B testing implementation',
        'User behavior tracking',
        'Conversion funnel optimization',
        'Performance reporting'
      ],
      price: '1500'
    },
    {
      title: 'Ad-Performance Data Visualization',
      duration: '1-2 weeks',
      description: [
        'Clean data pipeline setup',
        'Interactive on-site charts',
        'Real-time data integration',
        'Custom dashboard design',
        'Performance metrics display'
      ],
      price: '200'
    }
  ];

  const timeline = [
    {
      week: 'Week 1-2',
      title: 'Foundation & Setup',
      tasks: [
        'GA4 & Tag Manager configuration',
        'Initial audit and strategy planning',
        'Landing page optimization begins'
      ]
    },
    {
      week: 'Week 3-4',
      title: 'Content & Ads Launch',
      tasks: [
        'Google Ads campaign setup',
        'First SEO blog posts published',
        'Landing page polish completed'
      ]
    },
    {
      week: 'Week 5-6',
      title: 'Optimization & Refinement',
      tasks: [
        'Ad performance optimization',
        'Additional content creation',
        'Performance dashboard setup'
      ]
    },
    {
      week: 'Week 7-8',
      title: 'Analysis & Handoff',
      tasks: [
        'Final performance analysis',
        'Strategy recommendations',
        'Complete documentation handoff'
      ]
    }
  ];

  return (
    <div className={styles.marketingPlanLayout}>
      {/* Sidebar */}
      <aside className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarOpen : ''}`}>
        <div className={styles.sidebarHeader}>
          <div className={styles.logo}>
            <div className={styles.logoIcon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span className={styles.logoText}>Contract-Well</span>
          </div>
        </div>
        
        <nav className={styles.sidebarNav}>
          <div className={styles.navSection}>
            <span className={styles.navSectionTitle}>Main</span>
            <ul className={styles.navList}>
              <li className={styles.navItem}>
                <button className={styles.navLink} onClick={handleBackToDashboard}>
                  <svg className={styles.navIcon} width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Dashboard</span>
                </button>
              </li>
              <li className={styles.navItem}>
                <button 
                  className={styles.navLink}
                  onClick={() => window.open('https://contractwell.ca', '_blank')}
                >
                  <svg className={styles.navIcon} width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M18 13V6C18 5.46957 17.7893 4.96086 17.4142 4.58579C17.0391 4.21071 16.5304 4 16 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V18C2 18.5304 2.21071 19.0391 2.58579 19.4142C2.96086 19.7893 3.46957 20 4 20H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M15 15L21 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 9H21V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Platform Access</span>
                </button>
              </li>
            </ul>
          </div>
          
          <div className={styles.navSection}>
            <span className={styles.navSectionTitle}>Services</span>
            <ul className={styles.navList}>
              <li className={`${styles.navItem} ${styles.active}`}>
                <div className={styles.navLink}>
                  <svg className={styles.navIcon} width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Marketing Solutions</span>
                </div>
              </li>
              <li className={styles.navItem}>
                <button 
                  className={styles.navLink}
                  onClick={() => navigate(`/site-addons/${token}`)}
                >
                  <svg className={styles.navIcon} width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Site Add-ons</span>
                </button>
              </li>
            </ul>
          </div>
          
          <div className={styles.navSection}>
            <span className={styles.navSectionTitle}>Administration</span>
            <ul className={styles.navList}>
              <li className={styles.navItem}>
                <button className={styles.navLink} onClick={() => alert('This page is not set up for contract-well yet. Contact us if you think something should be updated.')}>
                  <svg className={styles.navIcon} width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M3 13L9 7L13 11L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M21 21H3L3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Analytics</span>
                </button>
              </li>
              <li className={styles.navItem}>
                <button className={styles.navLink} onClick={() => alert('This page is not set up for contract-well yet. Contact us if you think something should be updated.')}>
                  <svg className={styles.navIcon} width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.2579 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.01127 9.77251C4.28054 9.5799 4.48571 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.87653 6.85425 4.02405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29583 4.55324 9.54799 4.34276 9.72569 4.07447C9.90339 3.80618 9.99871 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29583 19.6572 9.54799 19.9255 9.72569C20.1938 9.90339 20.5082 9.99871 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Settings</span>
                </button>
              </li>
              <li className={styles.navItem}>
                <button className={styles.navLink} onClick={() => alert('This page is not set up for contract-well yet. Contact us if you think something should be updated.')}>
                  <svg className={styles.navIcon} width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>User Management</span>
                </button>
              </li>
            </ul>
          </div>
        </nav>
      </aside>

      {/* Main Content Area */}
      <div className={styles.mainContent}>
        {/* Top Navigation */}
        <header className={styles.topNav}>
          <div className={styles.topNavLeft}>
            <button className={styles.sidebarToggle} onClick={toggleSidebar}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className={styles.breadcrumb}>
              <span className={styles.breadcrumbItem}>Services</span>
              <span className={styles.breadcrumbSeparator}>/</span>
              <span className={styles.breadcrumbItem}>Marketing Solutions</span>
            </div>
          </div>
          
          <div className={styles.topNavRight}>
            <div className={styles.statusIndicator}>
              <div className={styles.statusDot}></div>
              <span className={styles.statusText}>Online</span>
            </div>
            <button onClick={handleLogout} className={styles.logoutButton}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Sign Out</span>
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className={styles.pageContent}>
          <div className={styles.contentHeader}>
            <h1 className={styles.pageTitle}>Marketing & Enhancement Proposal</h1>
            <p className={styles.pageDescription}>
              Professional marketing solutions designed specifically for Contract-Well. All prices in CAD.
            </p>
          </div>

          <div className={styles.contentGrid}>
            {/* Timeline Section */}
            <div className={styles.timelineSection}>
              <h2 className={styles.sectionTitle}>6-Week Implementation Timeline</h2>
              <div className={styles.timeline}>
                {timeline.map((phase, index) => (
                  <div key={index} className={styles.timelineItem}>
                    <div className={styles.timelineMarker}>
                      <div className={styles.timelineDot}></div>
                      {index < timeline.length - 1 && <div className={styles.timelineLine}></div>}
                    </div>
                    <div className={styles.timelineContent}>
                      <div className={styles.timelineWeek}>{phase.week}</div>
                      <h3 className={styles.timelineTitle}>{phase.title}</h3>
                      <ul className={styles.timelineTasks}>
                        {phase.tasks.map((task, taskIndex) => (
                          <li key={taskIndex}>{task}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Services Section */}
            <div className={styles.servicesSection}>
              <h2 className={styles.sectionTitle}>Available Services</h2>
              <div className={styles.servicesGrid}>
                {availableServices.map((service, index) => (
                  <div key={index} className={styles.serviceCard}>
                    <div className={styles.serviceHeader}>
                      <div>
                        <h3 className={styles.serviceTitle}>{service.title}</h3>
                        <div className={styles.serviceDuration}>{service.duration}</div>
                      </div>
                      {service.isBundle && (
                        <div className={styles.bundleBadge}>
                          Bundle Package - Save ${service.savings}
                        </div>
                      )}
                    </div>
                    <div className={styles.serviceContent}>
                      <ul className={styles.serviceFeatures}>
                        {service.description.map((feature, featureIndex) => (
                          <li key={featureIndex}>{feature}</li>
                        ))}
                      </ul>
                    </div>
                    <div className={styles.serviceFooter}>
                      {service.isBundle ? (
                        <div className={styles.bundlePricing}>
                          <div className={styles.originalPrice}>${service.originalPrice} CAD</div>
                          <div className={styles.bundlePrice}>${service.price} CAD</div>
                        </div>
                      ) : (
                        <div className={styles.servicePrice}>${service.price} CAD</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>


        </main>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && <div className={styles.sidebarOverlay} onClick={toggleSidebar}></div>}
    </div>
  );
};

export default MarketingPlan; 